import React, { useState, useEffect, useRef } from 'react';
import { 
  View, 
  Text, 
  TouchableOpacity, 
  StyleSheet, 
  Animated, 
  Easing, 
  Platform, 
  Image,
  TextInput,
  ActivityIndicator,
  Dimensions,
  ScrollView
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { colors } from '../styles/theme';
import { homeStyles } from '../styles/HomeStyles';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../config/mapbox-config';

// Custom LinearGradient component since expo-linear-gradient is not available
const LinearGradient = ({ colors, style, children }) => {
  if (Platform.OS === 'web') {
    return (
      <View
        style={{
          ...style,
          background: `linear-gradient(to bottom, ${colors.join(', ')})`,
        }}
      >
        {children}
      </View>
    );
  }
  
  // Fallback for non-web platforms (should not reach here in this web app)
  return <View style={style}>{children}</View>;
};

// Set Mapbox access token
if (Platform.OS === 'web') {
  mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;
}

// TourPlanner icon components
const TourIcon = () => (
  <View style={styles.iconContainer}>
    <Text style={styles.iconText}>🚌</Text>
  </View>
);

const GasIcon = () => (
  <View style={styles.iconContainer}>
    <Text style={styles.iconText}>⛽</Text>
  </View>
);

export function LandingScreen() {
  const navigation = useNavigation();
  const [hoveredOption, setHoveredOption] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  
  // Animation refs
  const fadeAnim = useRef(new Animated.Value(0)).current;
  const slideUpAnim = useRef(new Animated.Value(50)).current;
  const logoFadeAnim = useRef(new Animated.Value(0)).current;
  const logoScaleAnim = useRef(new Animated.Value(0.9)).current;
  
  // Home screen animation refs
  const titleFadeRef = useRef(new Animated.Value(0)).current;
  const titleSlideRef = useRef(new Animated.Value(30)).current;
  const subtitleFadeRef = useRef(new Animated.Value(0)).current;
  const subtitleSlideRef = useRef(new Animated.Value(30)).current;
  const searchFadeRef = useRef(new Animated.Value(0)).current;
  const searchSlideRef = useRef(new Animated.Value(30)).current;

  const [windowDimensions, setWindowDimensions] = useState(
    Platform.OS === 'web' ? { width: window.innerWidth, height: window.innerHeight } : Dimensions.get('window')
  );

  // Add window resize handler for web
  useEffect(() => {
    if (Platform.OS === 'web') {
      const handleResize = () => {
        setWindowDimensions({ width: window.innerWidth, height: window.innerHeight });
      };
      
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  useEffect(() => {
    // Run entrance animations
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 800,
        useNativeDriver: true,
        easing: Easing.out(Easing.cubic),
      }),
      Animated.timing(slideUpAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
        easing: Easing.out(Easing.cubic),
      }),
      Animated.sequence([
        Animated.delay(300),
        Animated.parallel([
          Animated.timing(logoFadeAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
          }),
          Animated.timing(logoScaleAnim, {
            toValue: 1,
            duration: 1000,
            useNativeDriver: true,
            easing: Easing.out(Easing.back(1.5)),
          }),
        ]),
      ]),
    ]).start();
    
    // Start Home animations
    const DURATION = 400;
    Animated.parallel([
      Animated.timing(titleFadeRef, {
        toValue: 1,
        duration: DURATION,
        useNativeDriver: true
      }),
      Animated.timing(titleSlideRef, {
        toValue: 0,
        duration: DURATION,
        useNativeDriver: true
      }),
      Animated.timing(subtitleFadeRef, {
        toValue: 1,
        duration: DURATION,
        useNativeDriver: true
      }),
      Animated.timing(subtitleSlideRef, {
        toValue: 0,
        duration: DURATION,
        useNativeDriver: true
      }),
      Animated.timing(searchFadeRef, {
        toValue: 1,
        duration: DURATION,
        useNativeDriver: true
      }),
      Animated.timing(searchSlideRef, {
        toValue: 0,
        duration: DURATION,
        useNativeDriver: true
      })
    ]).start();
    
    // Initialize map for Tour Planner preview if on web
    if (Platform.OS === 'web' && mapContainerRef.current) {
      try {
        mapRef.current = new mapboxgl.Map({
          container: mapContainerRef.current,
          style: 'mapbox://styles/mapbox/dark-v10',
          center: [-98.5795, 39.8283], // Center of the US
          zoom: 3,
          interactive: false, // Disable interactions for preview
        });
        
        // Add a sample route line for visual effect
        mapRef.current.on('load', () => {
          // This is just for visual preview, not functional
          mapRef.current.addSource('route', {
            'type': 'geojson',
            'data': {
              'type': 'Feature',
              'properties': {},
              'geometry': {
                'type': 'LineString',
                'coordinates': [
                  [-122.4194, 37.7749], // San Francisco
                  [-118.2437, 34.0522], // Los Angeles
                  [-115.1398, 36.1699], // Las Vegas
                  [-112.0740, 33.4484], // Phoenix
                  [-106.6504, 35.0844], // Albuquerque
                  [-104.9903, 39.7392], // Denver
                  [-95.7129, 37.0902],  // Kansas City
                  [-90.1994, 38.6270],  // St. Louis
                  [-87.6298, 41.8781],  // Chicago
                  [-79.9959, 40.4406],  // Pittsburgh
                  [-74.0060, 40.7128],  // New York
                ]
              }
            }
          });
          
          mapRef.current.addLayer({
            'id': 'route',
            'type': 'line',
            'source': 'route',
            'layout': {
              'line-join': 'round',
              'line-cap': 'round'
            },
            'paint': {
              'line-color': '#3498db',
              'line-width': 4,
              'line-opacity': 0.7
            }
          });
        });
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    }
    
    return () => {
      // Clean up map on unmount
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
  }, []);

  const navigateToTourPlanner = () => {
    navigation.navigate('TourPlanner');
  };

  const navigateToHome = () => {
    navigation.navigate('Home');
  };
  
  // Mini version of handleNearMe from Home.js
  const handleNearMe = () => {};

  // Determine if we're on a small screen for responsive layout
  const isSmallScreen = windowDimensions.width < 1024;
  
  return (
    <View style={styles.container}>
      <BackgroundVideo />
      <ScrollView style={{ width: '100%' }}>
        <View style={styles.overlay}>
          <View style={[styles.logoContainer, isSmallScreen && { marginBottom: 20 }]}>
            <Animated.Text 
              style={[
                styles.welcomeHeader,
                isSmallScreen && { fontSize: 32 },
                { opacity: logoFadeAnim, transform: [{ translateY: slideUpAnim }] }
              ]}
            >
              Welcome to Venue Report Card
            </Animated.Text>
            <Animated.Text 
              style={[
                styles.subHeader,
                isSmallScreen && { fontSize: 14 },
                { opacity: logoFadeAnim, transform: [{ translateY: slideUpAnim }] }
              ]}
            >
              Your trusted resource for venue information and tour planning
            </Animated.Text>
          </View>
          
          <Animated.View 
            style={[
              styles.optionsContainer,
              isSmallScreen && { 
                flexDirection: 'column',
                paddingHorizontal: 10,
                paddingTop: 10
              },
              { opacity: fadeAnim, transform: [{ translateY: slideUpAnim }] }
            ]}
          >
{/*             
            <TouchableOpacity
              style={[
                styles.optionCard,
                styles.tourPlannerCard,
                hoveredOption === 'tourPlanner' && styles.hoveredCard,
                isSmallScreen && { 
                  maxWidth: '100%', 
                  minWidth: '100%',
                  height: 400,
                  marginBottom: 30
                }
              ]}
              onPress={navigateToTourPlanner}
              onMouseEnter={() => setHoveredOption('tourPlanner')}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, isSmallScreen && { fontSize: 22 }]}>Tour Planner</Text>
              </View>
              
              <View style={[styles.previewContainer, isSmallScreen && { height: 200 }]}>
                <View
                  ref={mapContainerRef}
                  style={styles.mapPreview}
                />
                
                <LinearGradient
                  colors={['rgba(0,0,0,0)', 'rgba(0,0,0,0.8)']}
                  style={styles.tourControlsPreview}
                >
                  <View style={styles.tourStatContainer}>
                    <View style={styles.tourStat}>
                      <TourIcon />
                      <Text style={styles.tourStatLabel}>TOTAL DISTANCE</Text>
                      <Text style={styles.tourStatValue}>3,245 mi</Text>
                    </View>
                    
                    <View style={styles.tourStat}>
                      <GasIcon />
                      <Text style={styles.tourStatLabel}>EST. FUEL COST</Text>
                      <Text style={styles.tourStatValue}>$685</Text>
                    </View>
                  </View>
                </LinearGradient>
              </View>
              
              <View style={styles.cardContent}>
                <Text style={[styles.cardDescription, isSmallScreen && { fontSize: 14, lineHeight: 20 }]}>
                  Plan your tours efficiently with our interactive map tool. Calculate distances, estimate costs, and find venues along your route.
                </Text>
                <View style={[styles.cardButton, styles.tourPlannerButton]}>
                  <Text style={styles.buttonText}>Start Planning</Text>
                </View>
              </View>
            </TouchableOpacity>
             */}
            <TouchableOpacity
              style={[
                styles.optionCard,
                styles.venueReportCard,
                hoveredOption === 'venueReport' && styles.hoveredCard,
                isSmallScreen && { 
                  maxWidth: '100%', 
                  minWidth: '100%',
                  height: 400,
                  marginBottom: 30
                }
              ]}
              onPress={navigateToHome}
              onMouseEnter={() => setHoveredOption('venueReport')}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, isSmallScreen && { fontSize: 22 }]}>Venue Report Card</Text>
              </View>
              
              <View style={[styles.previewContainer, isSmallScreen && { height: 200 }]}>
                {/* Home Screen Preview */}
                <View style={styles.homePreview}>
                  <Animated.Text 
                    style={[
                      styles.homePreviewTitle,
                      {
                        opacity: titleFadeRef,
                        transform: [{ translateY: titleSlideRef }]
                      }
                    ]}
                  >
                    VENUE{'\n'}
                    REPORT{'\n'}
                    CARD
                  </Animated.Text>
                  
                  <Animated.Text 
                    style={[
                      styles.homePreviewSubtitle,
                      {
                        opacity: subtitleFadeRef,
                        transform: [{ translateY: subtitleSlideRef }]
                      }
                    ]}
                  >
                    Search venues by name or location
                  </Animated.Text>

                  <Animated.View 
                    style={[
                      styles.homePreviewSearchContainer,
                      {
                        opacity: searchFadeRef,
                        transform: [{ translateY: searchSlideRef }]
                      }
                    ]}
                  >
                    <TextInput
                      style={styles.homePreviewSearchInput}
                      placeholder="Search venue name or location"
                      placeholderTextColor="#ffffff80"
                      value={searchText}
                      onChangeText={setSearchText}
                      editable={false}
                    />
                  </Animated.View>
                </View>
              </View>
              
              <View style={styles.cardContent}>
                <Text style={[styles.cardDescription, isSmallScreen && { fontSize: 14, lineHeight: 20 }]}>
                  Find and review venues. Read honest ratings from artists, employees, and crew members.
                </Text>
                <View style={[styles.cardButton, styles.venueReportButton]}>
                  <Text style={styles.buttonText}>Explore Venues</Text>
                </View>
              </View>
            </TouchableOpacity>
            
            <TouchableOpacity
              style={[
                styles.optionCard,
                styles.communityCard,
                hoveredOption === 'community' && styles.hoveredCard,
                isSmallScreen && { 
                  maxWidth: '100%', 
                  minWidth: '100%',
                  height: 400,
                  marginBottom: 30
                }
              ]}
              onMouseEnter={() => setHoveredOption('community')}
              onMouseLeave={() => setHoveredOption(null)}
            >
              <View style={styles.cardHeader}>
                <Text style={[styles.cardTitle, isSmallScreen && { fontSize: 22 }]}>Community</Text>
              </View>
              
              <View style={[styles.previewContainer, isSmallScreen && { height: 200 }]}>
                <View style={styles.communityPreview}>
                  <Image
                    source={require('../../assets/logo.png')}
                    style={styles.communityLogo}
                    resizeMode="contain"
                  />
                  <Text style={styles.communityText}>Connect with other musicians</Text>
                </View>
                
                {hoveredOption === 'community' && (
                  <View style={styles.comingSoonOverlay}>
                    <Text style={styles.comingSoonText}>Coming Soon!</Text>
                    <Text style={styles.comingSoonSubtext}>
                      We're working on building a community platform for musicians to connect, share experiences, and collaborate.
                    </Text>
                  </View>
                )}
              </View>
              
              <View style={styles.cardContent}>
                <Text style={[styles.cardDescription, isSmallScreen && { fontSize: 14, lineHeight: 20 }]}>
                  Connect with other musicians and industry professionals. Share experiences and collaborate.
                </Text>
                <View style={[styles.cardButton, styles.communityButton]}>
                  <Text style={styles.buttonText}>Join Community</Text>
                </View>
              </View>
            </TouchableOpacity>
          </Animated.View>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#E9E9E9',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    width: '100%',
  },
  overlay: {
    flex: 1,
    backgroundColor: '#E9E9E9',
    width: '100%',
    minHeight: '100vh',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 80,
  },
  logoContainer: {
    marginBottom: 40,
    alignItems: 'center',
    width: '100%',
    maxWidth: 800,
    paddingHorizontal: 20,
  },
  welcomeHeader: {
    fontFamily: 'DazHandwriting',
    fontSize: Platform.OS === 'web' ? 42 : 32,
    color: '#000000',
    textAlign: 'center',
    marginBottom: 10,
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
  },
  subHeader: {
    fontFamily: 'VastagoGrotesk',
    fontSize: Platform.OS === 'web' ? 18 : 16,
    color: '#000000',
    textAlign: 'center',
  },
  optionsContainer: {
    flexDirection: Platform.OS === 'web' ? 'row' : 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    maxWidth: 1400,
    gap: 20,
    flexWrap: 'wrap',
    ...(Platform.OS === 'web' ? { paddingHorizontal: 20, paddingVertical: 0 } : { padding: 0 }),
  },
  optionCard: {
    flex: Platform.OS === 'web' ? 1 : undefined,
    maxWidth: Platform.OS === 'web' ? 400 : '100%',
    minWidth: Platform.OS === 'web' ? 300 : '100%',
    height: Platform.OS === 'web' ? 600 : 500,
    borderRadius: 20,
    overflow: 'hidden',
    position: 'relative',
    marginBottom: 20,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
      transition: 'all 0.3s ease-out',
      WebkitTransition: 'all 0.3s ease-out',
      MozTransition: 'all 0.3s ease-out',
      boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
    }),
    display: 'flex',
    flexDirection: 'column',
  },
  hoveredCard: {
    ...(Platform.OS === 'web' && {
      transform: 'translateY(-10px) scale(1.02)',
      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.5)',
      transition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
      WebkitTransition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
      MozTransition: 'transform 0.3s ease-out, box-shadow 0.3s ease-out',
    }),
  },
  cardHeader: {
    padding: 20,
    alignItems: 'center',
  },
  tourPlannerCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    ...(Platform.OS === 'web' && {
      ':hover': {
        boxShadow: '0 20px 40px rgba(52, 152, 219, 0.3)',
      }
    }),
  },
  venueReportCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    ...(Platform.OS === 'web' && {
      ':hover': {
        boxShadow: '0 20px 40px rgba(155, 89, 182, 0.3)',
      }
    }),
  },
  previewContainer: {
    width: '100%',
    height: Platform.OS === 'web' ? 300 : 200,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
  },
  mapPreview: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: '#1a1a1a',
  },
  tourControlsPreview: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: '100%',
    padding: 15,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  tourStatContainer: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    marginTop: 5,
  },
  tourStat: {
    alignItems: 'center',
  },
  tourStatLabel: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: 12,
    marginBottom: 2,
    fontFamily: 'VastagoGrotesk',
  },
  tourStatValue: {
    color: 'white',
    fontSize: 14,
    fontWeight: '600',
    fontFamily: 'VastagoGrotesk',
  },
  iconContainer: {
    height: 24,
    width: 24,
    marginBottom: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconText: {
    fontSize: 20,
  },
  homePreview: {
    width: '100%',
    height: '100%',
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  homePreviewTitle: {
    fontFamily: 'DazHandwriting',
    fontWeight: '900',
    fontSize: Platform.OS === 'web' ? 32 : 28,
    color: '#c7cc4c',
    textAlign: 'center',
    marginBottom: 10,
    textShadow: '0px 2px 2px rgba(0,0,0,0.25)',
  },
  homePreviewSubtitle: {
    fontFamily: 'VastagoGrotesk',
    fontWeight: '500',
    fontSize: 14,
    color: 'white',
    textAlign: 'center',
    marginBottom: 10,
  },
  homePreviewSearchContainer: {
    width: '90%',
    marginTop: 10,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(255, 255, 255, 0.5)',
  },
  homePreviewSearchInput: {
    backgroundColor: 'transparent',
    color: 'white',
    width: '100%',
    fontSize: 14,
    height: 40,
    paddingHorizontal: 10,
    fontFamily: 'VastagoGrotesk',
    ...(Platform.OS === 'web' && {
      outlineStyle: 'none',
      border: 'none',
    }),
  },
  cardContent: {
    padding: 20,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
    textAlign: 'center',
    flex: 1,
  },
  cardTitle: {
    fontFamily: 'DazHandwriting',
    fontSize: Platform.OS === 'web' ? 28 : 24,
    color: 'white',
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
  },
  cardDescription: {
    fontFamily: 'VastagoGrotesk',
    fontSize: Platform.OS === 'web' ? 16 : 14,
    color: 'white',
    textAlign: 'center',
    marginBottom: 20,
    lineHeight: Platform.OS === 'web' ? 22 : 20,
  },
  cardButton: {
    padding: 12,
    borderRadius: 30,
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 150,
  },
  tourPlannerButton: {
    backgroundColor: '#2980b9',
  },
  venueReportButton: {
    backgroundColor: '#8e44ad',
  },
  buttonText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: 'white',
    fontWeight: '600',
  },
  communityCard: {
    backgroundColor: 'rgba(0, 0, 0, 0.8)',
    ...(Platform.OS === 'web' && {
      ':hover': {
        boxShadow: '0 20px 40px rgba(46, 204, 113, 0.3)',
      }
    }),
  },
  communityPreview: {
    width: '100%',
    height: '100%',
    padding: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  communityLogo: {
    width: 100,
    height: 100,
    marginBottom: 20,
  },
  communityText: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 20,
    color: 'white',
    textAlign: 'center',
    marginBottom: 10,
  },
  comingSoonOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 10,
    padding: 20,
  },
  comingSoonText: {
    fontFamily: 'DazHandwriting',
    fontSize: 40,
    color: '#2ecc71',
    marginBottom: 15,
    textAlign: 'center',
    textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
  },
  comingSoonSubtext: {
    fontFamily: 'VastagoGrotesk',
    fontSize: 16,
    color: 'white',
    textAlign: 'center',
    lineHeight: 24,
    maxWidth: 300,
  },
  communityButton: {
    backgroundColor: '#27ae60',
  },
}); 